const CLOCK_OFFSET_KEY = 'clockOffset';

let cachedClockOffset: number | null = null;
let hasClockOffsetStorageListener = false;
const clockOffsetSubscribers = new Set<(offsetMs: number) => void>();

function parseOffsetValue(raw: string | null): number {
  if (!raw) {
    return 0;
  }
  const parsed = parseInt(raw, 10);
  if (isNaN(parsed)) {
    console.warn(`Invalid clock offset value in localStorage: "${raw}"`);
    return 0;
  }
  return parsed;
}

function notifyClockOffsetSubscribers() {
  const value = cachedClockOffset ?? 0;
  for (const callback of clockOffsetSubscribers) {
    callback(value);
  }
}

function ensureClockOffsetInitialized() {
  if (cachedClockOffset === null) {
    if (typeof window === 'undefined') {
      cachedClockOffset = 0;
    } else {
      cachedClockOffset = parseOffsetValue(
        localStorage.getItem(CLOCK_OFFSET_KEY),
      );
    }
  }

  if (typeof window !== 'undefined' && !hasClockOffsetStorageListener) {
    window.addEventListener('storage', event => {
      if (event.key !== CLOCK_OFFSET_KEY) {
        return;
      }
      const next = parseOffsetValue(event.newValue);
      if (next === cachedClockOffset) {
        return;
      }
      cachedClockOffset = next;
      notifyClockOffsetSubscribers();
    });
    hasClockOffsetStorageListener = true;
  }
}

export function subscribeClockOffset(
  callback: (offsetMs: number) => void,
): () => void {
  ensureClockOffsetInitialized();
  clockOffsetSubscribers.add(callback);
  callback(cachedClockOffset ?? 0);

  return () => {
    clockOffsetSubscribers.delete(callback);
  };
}

export function getClockOffset(): number {
  ensureClockOffsetInitialized();
  return cachedClockOffset ?? 0;
}

export function setClockOffset(offsetMs: number): void {
  ensureClockOffsetInitialized();
  const normalized = Number.isFinite(offsetMs) ? Math.round(offsetMs) : 0;
  if (typeof window !== 'undefined') {
    localStorage.setItem(CLOCK_OFFSET_KEY, String(normalized));
  }
  if (cachedClockOffset === normalized) {
    return;
  }
  cachedClockOffset = normalized;
  notifyClockOffsetSubscribers();
}

export function nowWithOffsetMs(): number {
  return Date.now() + getClockOffset();
}

export function nowWithOffsetDate(): Date {
  return new Date(nowWithOffsetMs());
}

export function timeNow(): string {
  return nowWithOffsetDate().toISOString();
}

export function formatClockOffset(offsetMs = getClockOffset()): string {
  const offsetMinutes = Math.round(offsetMs / 60000);
  const offsetSign = offsetMinutes >= 0 ? '+' : '-';
  const absMinutes = Math.abs(offsetMinutes);

  if (absMinutes === 0) {
    return 'No adjustment';
  }

  return `${offsetSign}${Math.floor(absMinutes / 60)
    .toString()
    .padStart(2, '0')}:${(absMinutes % 60).toString().padStart(2, '0')}`;
}

export function parseClockInputToOffsetMs(input: string): number | null {
  const match = input.trim().match(/^(\d{1,2})[:\s]?(\d{2})$/);
  if (!match) {
    return null;
  }
  const hours = parseInt(match[1]!, 10);
  const minutes = parseInt(match[2]!, 10);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  const baseNow = nowWithOffsetDate();
  const target = new Date(baseNow);
  target.setHours(hours, minutes, 0, 0);
  return target.getTime() - Date.now();
}
